import React, { useState, useEffect, useRef } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input, FormFeedback, Button, Row, Col } from 'reactstrap';
import { handleAddModifyOperation, fetchTableData } from '../../../../Api/ApiMethods';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { XSquare } from 'react-feather';

const toLocalISOString = (date) => {
    if (!date) return '';
    try {
        const dateObj = new Date(date);
        if (isNaN(dateObj.getTime())) {
            console.warn("Invalid date provided to toLocalISOString:", date);
            return '';
        }
        const tzOffset = dateObj.getTimezoneOffset() * 60000;
        const localDate = new Date(dateObj.getTime() - tzOffset);
        return localDate.toISOString().slice(0, 16);
    } catch (error) {
        console.error("Error formatting date in toLocalISOString:", error);
        return '';
    }
};

const formatCurrency = (value) => {
    if (value === null || value === undefined) return '';
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(value);
}

const ExpenseDataModal = ({ apiEndpoint, isOpen, toggle, mode, selectedExpense, refreshData, customerId, autoAssignCustomer = false, accidentFile = [], callformId, readOnlyView = false, initialCustomerContractRate = null, initialIsMiddlemanContractBased = false, initialMiddlemanContractRate = null, initialMiddlemanContractPrice = null, relatedArbitrationSchema = [], arbitrationSchemaType = null }) => {
    const initialExpenseDataRef = useRef({
        expenseNumber: '',
        expenseType: '',
        amount: '',
        associatedBank: null,
        date: '',
        paymentStatus: 'Ödendi',
        invoiceNumber: '',
        description: '',
        accidentFile: [],
        customer: [],
        callForm: [],
        customerContractRate: null,
        isMiddlemanContractBased: false,
        middlemanContractRate: null,
        middlemanContractPrice: null,
        relatedArbitrationSchema: [],
        arbitrationSchemaType: null,
    });

    const [expenseData, setExpenseData] = useState(initialExpenseDataRef.current);
    const [errors, setErrors] = useState({});
    const [banks, setBanks] = useState([]);
    const bankApiUrl = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/bank';

    useEffect(() => {
        const fetchBanks = async () => {
            if (isOpen && bankApiUrl && !readOnlyView) {
                try {
                    const response = await fetchTableData({
                        apiEndpoint: bankApiUrl,
                        page: 1,
                        pageSize: 1000,
                        setDataCallback: (apiResponse) => {
                            const items = apiResponse?.data?.items || apiResponse?.items || (Array.isArray(apiResponse) ? apiResponse : []);
                            setBanks(items);
                        },
                        setTotalRowsCallback: () => { }
                    });
                } catch (error) {
                    console.error("Bank fetch error:", error);
                    toast.error("Bankalar yüklenirken bir hata oluştu.");
                    setBanks([]);
                }
            }
        };
        fetchBanks();
    }, [isOpen, bankApiUrl, readOnlyView]);

    useEffect(() => {
        if (isOpen) {
            if ((mode === 'modify' || readOnlyView) && selectedExpense) {
                const formattedDate = toLocalISOString(selectedExpense.date || new Date());
                const bankId = Array.isArray(selectedExpense.associatedBank) && selectedExpense.associatedBank.length > 0
                    ? selectedExpense.associatedBank[0]
                    : selectedExpense.associatedBank?.id || '';
                const accidentFiles = Array.isArray(selectedExpense.accidentFile) ? selectedExpense.accidentFile : [];
                const customers = autoAssignCustomer && customerId
                    ? [customerId]
                    : (Array.isArray(selectedExpense.customer) ? selectedExpense.customer : []);
                const callForms = callformId
                    ? [callformId]
                    : (Array.isArray(selectedExpense.callForm) ? selectedExpense.callForm : []);
                const relatedArbitrations = Array.isArray(selectedExpense.relatedArbitrationSchema) ? selectedExpense.relatedArbitrationSchema : [];

                setExpenseData({
                    ...initialExpenseDataRef.current,
                    ...selectedExpense,
                    date: formattedDate,
                    associatedBank: bankId,
                    accidentFile: accidentFiles.map(id => String(id)),
                    customer: customers.map(id => String(id)),
                    callForm: callForms.map(id => String(id)),
                    relatedArbitrationSchema: relatedArbitrations.map(id => String(id)),
                    arbitrationSchemaType: selectedExpense.arbitrationSchemaType || null,
                    amount: String(selectedExpense.amount || ''),
                    invoiceNumber: String(selectedExpense.invoiceNumber || ''),
                    expenseType: selectedExpense.expenseType || '',
                    customerContractRate: selectedExpense.customerContractRate || null,
                    isMiddlemanContractBased: selectedExpense.isMiddlemanContractBased === true,
                    middlemanContractRate: selectedExpense.isMiddlemanContractBased === true ? (selectedExpense.middlemanContractRate || null) : null,
                    middlemanContractPrice: selectedExpense.isMiddlemanContractBased === false ? (selectedExpense.middlemanContractPrice || null) : null,
                });
            } else if (mode === 'add' && !readOnlyView) {
                const newExpenseData = {
                    ...initialExpenseDataRef.current,
                    date: '',
                    accidentFile: accidentFile ? accidentFile.map(id => String(id)) : [],
                    customer: autoAssignCustomer && customerId ? [String(customerId)] : [],
                    callForm: callformId ? [String(callformId)] : [],
                    relatedArbitrationSchema: relatedArbitrationSchema ? relatedArbitrationSchema.map(id => String(id)) : [],
                    arbitrationSchemaType: arbitrationSchemaType || null,
                    expenseType: arbitrationSchemaType === 'Tahkim Başvuru Ücreti Ödemesi' ? 'TAHKİM BAŞVURU ÜCRETİ ÖDEMESİ'
                        : arbitrationSchemaType === 'Bilirkişi Ödemesi' ? 'BİLİRKİŞİ ÖDEMESİ'
                            : arbitrationSchemaType === 'Islah Ödemesi' ? 'ISLAH ÖDEMESİ'
                                : arbitrationSchemaType === 'Karşı Vekalet Ödemesi' ? 'KARŞI VEKALET ÖDEMESİ'
                                    : arbitrationSchemaType === 'İtiraz Ödemesi' ? 'İTİRAZ ÖDEMESİ' : '',
                    customerContractRate: initialCustomerContractRate !== null ? initialCustomerContractRate : initialExpenseDataRef.current.customerContractRate,
                    isMiddlemanContractBased: initialIsMiddlemanContractBased,
                    middlemanContractRate: initialIsMiddlemanContractBased && initialMiddlemanContractRate !== null
                        ? initialMiddlemanContractRate
                        : (initialIsMiddlemanContractBased ? initialExpenseDataRef.current.middlemanContractRate : null),
                    middlemanContractPrice: !initialIsMiddlemanContractBased && initialMiddlemanContractPrice !== null
                        ? initialMiddlemanContractPrice
                        : (!initialIsMiddlemanContractBased ? initialExpenseDataRef.current.middlemanContractPrice : null),
                };
                setExpenseData(newExpenseData);
            }
            if (!readOnlyView) {
                setErrors({});
            }
        }
    }, [isOpen, mode, selectedExpense, accidentFile, customerId, autoAssignCustomer, callformId, readOnlyView, initialCustomerContractRate, initialIsMiddlemanContractBased, initialMiddlemanContractRate, initialMiddlemanContractPrice, relatedArbitrationSchema, arbitrationSchemaType]);

    const handleInputChange = (e) => {
        if (readOnlyView) return;
        const { name, value, type, checked } = e.target;
        let processedValue = type === 'checkbox' ? checked : value;

        const fieldsToUppercase = ['description'];

        if (fieldsToUppercase.includes(name) && typeof processedValue === 'string') {
            processedValue = processedValue.toLocaleUpperCase('tr-TR');
        }
        else if (['amount', 'invoiceNumber'].includes(name)) {
            processedValue = value.replace(/[^0-9.,]/g, '');
            if (name === 'invoiceNumber') {
                processedValue = value.replace(/[^0-9]/g, '');
            }
        } else if (name === 'isMiddlemanContractBased') {
            processedValue = value === 'true';
            setExpenseData(prev => ({
                ...prev,
                isMiddlemanContractBased: processedValue,
                middlemanContractRate: processedValue ? prev.middlemanContractRate : null,
                middlemanContractPrice: !processedValue ? prev.middlemanContractPrice : null,
            }));
            if (errors.middlemanContractRate) setErrors(prev => ({ ...prev, middlemanContractRate: '' }));
            if (errors.middlemanContractPrice) setErrors(prev => ({ ...prev, middlemanContractPrice: '' }));
            return;
        }

        setExpenseData(prev => ({
            ...prev,
            [name]: processedValue
        }));

        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const validateForm = () => {
        if (readOnlyView) return true;
        const validationErrors = {};

        if (!expenseData.amount?.toString().trim()) {
            validationErrors.amount = 'Tutar zorunludur.';
        } else {
            const numericValue = expenseData.amount.replace(/,/g, '.');
            if (isNaN(Number(numericValue)) || Number(numericValue) <= 0) {
                validationErrors.amount = 'Geçerli pozitif bir tutar giriniz.';
            }
        }

        if (!expenseData.date) {
            validationErrors.date = 'Tarih zorunludur.';
        } else {
            try {
                const inputDate = new Date(expenseData.date);
            } catch (e) {
                validationErrors.date = 'Geçersiz tarih formatı.';
            }
        }

        if (expenseData.invoiceNumber && !/^\d+$/.test(expenseData.invoiceNumber)) {
            validationErrors.invoiceNumber = 'Fatura numarası sadece rakam içermelidir.';
        }

        if (expenseData.description && expenseData.description.length > 500) {
            validationErrors.description = 'Açıklama en fazla 500 karakter olabilir.';
        }

        if (expenseData.customerContractRate !== null && (parseInt(expenseData.customerContractRate) < 0 || parseInt(expenseData.customerContractRate) > 100)) {
            validationErrors.customerContractRate = 'Müşteri sözleşme oranı 0-100 arasında olmalıdır.';
        }
        if (expenseData.isMiddlemanContractBased === true && expenseData.middlemanContractRate !== null && (parseInt(expenseData.middlemanContractRate) < 0 || parseInt(expenseData.middlemanContractRate) > 100)) {
            validationErrors.middlemanContractRate = 'Aracı sözleşme oranı 0-100 arasında olmalıdır.';
        }
        if (expenseData.isMiddlemanContractBased === false && expenseData.middlemanContractPrice !== null) {
            const price = String(expenseData.middlemanContractPrice).replace(/,/g, '.');
            if (isNaN(Number(price)) || Number(price) < 0) {
                validationErrors.middlemanContractPrice = 'Geçerli pozitif bir aracı sözleşme fiyatı giriniz.';
            }
        }

        setErrors(validationErrors);
        return Object.keys(validationErrors).length === 0;
    };

    const handleSave = async () => {
        if (readOnlyView) return;
        if (!validateForm()) {
            toast.error('Lütfen formdaki hataları düzeltin.');
            const firstErrorKey = Object.keys(errors).find(key => errors[key]);
            if (firstErrorKey) document.getElementById(firstErrorKey)?.focus();
            return;
        }

        const amountToSend = Number(String(expenseData.amount).replace(/,/g, '.')) || 0;
        const invoiceNumberToSend = expenseData.invoiceNumber ? Number(expenseData.invoiceNumber) : null;
        const dateToSend = expenseData.date ? new Date(expenseData.date).toISOString() : null;
        const bankIdToSend = expenseData.associatedBank ? [String(expenseData.associatedBank)] : [];
        const expenseTypeToSend = expenseData.expenseType === '' ? null : expenseData.expenseType;

        const payload = {
            expenseNumber: expenseData.expenseNumber || null,
            expenseType: expenseTypeToSend,
            amount: amountToSend,
            associatedBank: bankIdToSend,
            date: dateToSend,
            paymentStatus: 'Ödendi',
            invoiceNumber: invoiceNumberToSend,
            description: expenseData.description || '',
            accidentFile: Array.isArray(expenseData.accidentFile) ? expenseData.accidentFile.map(id => String(id)) : [],
            customer: Array.isArray(expenseData.customer) ? expenseData.customer.map(id => String(id)) : [],
            callForm: Array.isArray(expenseData.callForm) ? expenseData.callForm.map(id => String(id)) : [],
            relatedArbitrationSchema: Array.isArray(expenseData.relatedArbitrationSchema) ? expenseData.relatedArbitrationSchema.map(id => String(id)) : [],
            arbitrationSchemaType: expenseData.arbitrationSchemaType || null,
            customercontractrate: expenseData.customerContractRate ? parseInt(expenseData.customerContractRate) : null,
            ismiddlemancontractbased: expenseData.isMiddlemanContractBased,
            middlemancontractrate: expenseData.isMiddlemanContractBased && expenseData.middlemanContractRate ? parseInt(expenseData.middlemanContractRate) : null,
            middlemancontractprice: !expenseData.isMiddlemanContractBased && expenseData.middlemanContractPrice ? parseFloat(String(expenseData.middlemanContractPrice).replace(/,/g, '.')) : null,
            ...(mode === 'modify' && selectedExpense?.id && { id: selectedExpense.id })
        };

        console.log("Sending Payload:", payload);

        try {
            await handleAddModifyOperation({
                apiEndpoint,
                selectedItem: mode === 'modify' ? { id: selectedExpense?.id } : null,
                mode,
                data: payload,
                refreshDataCallback: refreshData,
                componentName: 'Masraf',
            });
            toggle();
        } catch (error) {
            console.error("Masraf kaydetme hatası:", error);
        }
    };

    const resetForm = () => {
        if (readOnlyView) return;
        setExpenseData({
            ...initialExpenseDataRef.current,
            date: '',
            accidentFile: accidentFile ? accidentFile.map(id => String(id)) : [],
            customer: autoAssignCustomer && customerId ? [String(customerId)] : [],
            callForm: callformId ? [String(callformId)] : [],
            relatedArbitrationSchema: relatedArbitrationSchema ? relatedArbitrationSchema.map(id => String(id)) : [],
            arbitrationSchemaType: arbitrationSchemaType || null,
            expenseType: arbitrationSchemaType === 'Tahkim Başvuru Ücreti Ödemesi' ? 'TAHKİM BAŞVURU ÜCRETİ ÖDEMESİ'
                : arbitrationSchemaType === 'Bilirkişi Ödemesi' ? 'BİLİRKİŞİ ÖDEMESİ'
                    : arbitrationSchemaType === 'Islah Ödemesi' ? 'ISLAH ÖDEMESİ'
                        : arbitrationSchemaType === 'Karşı Vekalet Ödemesi' ? 'KARŞI VEKALET ÖDEMESİ'
                            : arbitrationSchemaType === 'İtiraz Ödemesi' ? 'İTİRAZ ÖDEMESİ' : '',
            customerContractRate: initialExpenseDataRef.current.customerContractRate,
            isMiddlemanContractBased: initialExpenseDataRef.current.isMiddlemanContractBased,
            middlemanContractRate: initialExpenseDataRef.current.middlemanContractRate,
            middlemanContractPrice: initialExpenseDataRef.current.middlemanContractPrice,
        });
        setErrors({});
    };

    const handleCancel = () => {
        if (readOnlyView) {
            toggle();
            return;
        }
        resetForm();
        toggle();
    };

    return (
        <Modal isOpen={isOpen} toggle={handleCancel} backdrop="static">
            <ModalHeader toggle={handleCancel}>
                {readOnlyView ? 'Masraf Detayları' : (mode === 'add' ? 'Masraf Ekle' : 'Masraf Düzenle')}
            </ModalHeader>
            <ModalBody>
                <Form>
                    <FormGroup>
                        <Label for="expenseNumber">Masraf Numarası</Label>
                        <Input
                            type="text"
                            id="expenseNumber"
                            name="expenseNumber"
                            value={expenseData.expenseNumber || ''}
                            readOnly
                            disabled
                            placeholder="Sistem Tarafından Atanır"
                        />
                    </FormGroup>

                    <FormGroup>
                        <Label for="expenseType">Masraf Tipi {!readOnlyView && <span className="text-danger">*</span>}</Label>
                        <Input
                            type="select"
                            id="expenseType"
                            name="expenseType"
                            value={expenseData.expenseType || ''}
                            onChange={handleInputChange}
                            invalid={!readOnlyView && !!errors.expenseType}
                            disabled={readOnlyView ||
                                (arbitrationSchemaType === 'Tahkim Başvuru Ücreti Ödemesi') ||
                                (arbitrationSchemaType === 'Bilirkişi Ödemesi') ||
                                (arbitrationSchemaType === 'Islah Ödemesi') ||
                                (arbitrationSchemaType === 'Karşı Vekalet Ödemesi') ||
                                (arbitrationSchemaType === 'İtiraz Ödemesi')
                            }>
                            <option value="">Masraf Tipi Seçiniz</option>
                            <option value="NOTER ÜCRETİ">NOTER ÜCRETİ</option>
                            <option value="ARACI ÖDEMESİ">ARACI ÖDEMESİ</option>
                            <option value="SATIN ALMA BEDELİ">SATIN ALMA BEDELİ</option>
                            <option value="360 SORGU BEDELİ">360 SORGU BEDELİ</option>
                            <option value="EKSPER FATURASI">EKSPER FATURASI</option>
                            <option value="KEP BAŞVURU">KEP BAŞVURU</option>
                            <option value="TAHKİM HARCI">TAHKİM HARCI</option>
                            <option value="TAHKİM BAŞVURU ÜCRETİ ÖDEMESİ">TAHKİM BAŞVURU ÜCRETİ ÖDEMESİ</option>
                            <option value="BİLİRKİŞİ ÖDEMESİ">BİLİRKİŞİ ÖDEMESİ</option>
                            <option value="ISLAH ÖDEMESİ">ISLAH ÖDEMESİ</option>
                            <option value="İTİRAZ ÖDEMESİ">İTİRAZ ÖDEMESİ</option>
                            <option value="KARŞI VEKALET ÖDEMESİ">KARŞI VEKALET ÖDEMESİ</option>
                            <option value="BARO PULU">BARO PULU</option>
                            <option value="TAKİP AÇILIŞ MASRAFI">TAKİP AÇILIŞ MASRAFI</option>
                            <option value="HACİZ MASRAFI">HACİZ MASRAFI</option>
                            <option value="TAHSİL HARCI">TAHSİL HARCI</option>
                            <option value="CEZAEVİ HARCI">CEZAEVİ HARCI</option>
                        </Input>
                        {!readOnlyView && <FormFeedback>{errors.expenseType}</FormFeedback>}
                    </FormGroup>

                    <FormGroup>
                        <Label for="amount">Tutar {!readOnlyView && <span className="text-danger">*</span>}</Label>
                        <Input
                            type="text"
                            id="amount"
                            name="amount"
                            value={readOnlyView && expenseData.amount ? formatCurrency(expenseData.amount) : (expenseData.amount || '')}
                            onChange={handleInputChange}
                            placeholder="Örn: 150.75"
                            invalid={!readOnlyView && !!errors.amount}
                            readOnly={readOnlyView}
                        />
                        {!readOnlyView && <FormFeedback>{errors.amount}</FormFeedback>}
                    </FormGroup>

                    <FormGroup>
                        <Label for="associatedBank">İlgili Banka</Label>
                        <Input
                            type="select"
                            id="associatedBank"
                            name="associatedBank"
                            value={expenseData.associatedBank || ''}
                            onChange={handleInputChange}
                            invalid={!readOnlyView && !!errors.associatedBank}
                            disabled={readOnlyView}
                        >
                            <option value="">Banka Seçiniz (Opsiyonel)</option>
                            {banks?.map(bank => (
                                <option key={bank.id} value={String(bank.id)}>
                                    {bank.bankName} {bank.branchName ? `- ${bank.branchName}` : ''} (IBAN: {bank.iban})
                                </option>
                            ))}
                        </Input>
                        {!readOnlyView && <FormFeedback>{errors.associatedBank}</FormFeedback>}
                    </FormGroup>

                    {/* <FormGroup>
                        <Label for="paymentStatus">Ödeme Durumu</Label>
                        <Input
                            type="select"
                            id="paymentStatus"
                            name="paymentStatus"
                            value={expenseData.paymentStatus || 'Ödendi'}
                            onChange={handleInputChange}
                            invalid={!readOnlyView && !!errors.paymentStatus}
                            disabled={readOnlyView}
                        >
                            <option value="Ödenmedi">Ödenmedi</option>
                            <option value="Ödendi">Ödendi</option>
                        </Input>
                        {!readOnlyView && <FormFeedback>{errors.paymentStatus}</FormFeedback>}
                    </FormGroup> */}

                    <FormGroup>
                        <Label for="date">Tarih ve Saat {!readOnlyView && <span className="text-danger">*</span>}</Label>
                        <Input
                            type={readOnlyView ? "text" : "datetime-local"}
                            id="date"
                            name="date"
                            value={readOnlyView ? (expenseData.date ? new Date(expenseData.date).toLocaleString() : '') : (expenseData.date || '')}
                            onChange={handleInputChange}
                            invalid={!readOnlyView && !!errors.date}
                            readOnly={readOnlyView}
                        />
                        {!readOnlyView && <FormFeedback>{errors.date}</FormFeedback>}
                    </FormGroup>

                    <FormGroup>
                        <Label for="invoiceNumber">Fatura Numarası</Label>
                        <Input
                            type="text"
                            id="invoiceNumber"
                            name="invoiceNumber"
                            value={expenseData.invoiceNumber || ''}
                            onChange={handleInputChange}
                            placeholder="Fatura Numarası (Opsiyonel)"
                            invalid={!readOnlyView && !!errors.invoiceNumber}
                            readOnly={readOnlyView}
                        />
                        {!readOnlyView && <FormFeedback>{errors.invoiceNumber}</FormFeedback>}
                    </FormGroup>

                    {((callformId && mode === 'add') || (mode === 'modify' && expenseData.callForm && expenseData.callForm.length > 0)) && !readOnlyView && (expenseData.expenseType === 'ARACI ÖDEMESİ' || expenseData.expenseType === 'SATIN ALMA BEDELİ') && (
                        <>
                            <FormGroup>
                                <Label for="customerContractRate">Müşteri Sözleşme Oranı (%)</Label>
                                <Input
                                    type="number"
                                    id="customerContractRate"
                                    name="customerContractRate"
                                    value={expenseData.customerContractRate || ''}
                                    onChange={(e) => {
                                        let value = e.target.value;
                                        if (value && !/^\d+$/.test(value)) value = value.replace(/[^\d]/g, '');
                                        if (value && parseInt(value) > 100) value = '100';
                                        if (value && parseInt(value) < 0) value = '0';
                                        setExpenseData(prev => ({ ...prev, customerContractRate: value ? parseInt(value) : null }));
                                        if (errors.customerContractRate) setErrors(prev => ({ ...prev, customerContractRate: '' }));
                                    }}
                                    placeholder="0-100"
                                    invalid={!!errors.customerContractRate}
                                    disabled={readOnlyView}
                                />
                                <FormFeedback>{errors.customerContractRate}</FormFeedback>
                            </FormGroup>

                            <FormGroup className="mb-3">
                                <Row className="align-items-center">
                                    <Col xl={4} md={6}>
                                        <Label className="text-nowrap mb-0">Aracı Ödeme Türü</Label>
                                    </Col>
                                    <Col xl={8} md={6} className='mt-md-0 mt-2'>
                                        <div className="d-flex">
                                            <div className="radio radio-primary me-3 d-flex align-items-center">
                                                <Input
                                                    name="isMiddlemanContractBased"
                                                    type="radio"
                                                    value="true"
                                                    id="middlemanRateBased"
                                                    checked={expenseData.isMiddlemanContractBased === true}
                                                    onChange={handleInputChange}
                                                    disabled={readOnlyView}
                                                />
                                                <Label check for="middlemanRateBased" className="ms-1 mb-0">Oran Bazlı</Label>
                                            </div>
                                            <div className="radio radio-primary d-flex align-items-center">
                                                <Input
                                                    name="isMiddlemanContractBased"
                                                    type="radio"
                                                    value="false"
                                                    id="middlemanPriceBased"
                                                    checked={expenseData.isMiddlemanContractBased === false}
                                                    onChange={handleInputChange}
                                                    disabled={readOnlyView}
                                                />
                                                <Label check for="middlemanPriceBased" className="ms-1 mb-0">Fiyat Bazlı</Label>
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                            </FormGroup>

                            <Row>
                                <Col md={12}>
                                    {expenseData.isMiddlemanContractBased === true ? (
                                        <FormGroup>
                                            <Label for="middlemanContractRate">Aracı Sözleşme Oranı (%)</Label>
                                            <Input
                                                type="number"
                                                id="middlemanContractRate"
                                                name="middlemanContractRate"
                                                value={expenseData.middlemanContractRate || ''}
                                                onChange={(e) => {
                                                    let value = e.target.value;
                                                    if (value && !/^\d+$/.test(value)) value = value.replace(/[^\d]/g, '');
                                                    if (value && parseInt(value) > 100) value = '100';
                                                    if (value && parseInt(value) < 0) value = '0';
                                                    setExpenseData(prev => ({ ...prev, middlemanContractRate: value ? parseInt(value) : null }));
                                                    if (errors.middlemanContractRate) setErrors(prev => ({ ...prev, middlemanContractRate: '' }));
                                                }}
                                                min="0"
                                                max="100"
                                                placeholder="0-100"
                                                invalid={!!errors.middlemanContractRate}
                                                disabled={readOnlyView || expenseData.isMiddlemanContractBased === false}
                                            />
                                            <FormFeedback>{errors.middlemanContractRate}</FormFeedback>
                                        </FormGroup>
                                    ) : (
                                        <FormGroup>
                                            <Label for="middlemanContractPrice">Aracı Sözleşme Fiyatı (₺)</Label>
                                            <Input
                                                type="text"
                                                id="middlemanContractPrice"
                                                name="middlemanContractPrice"
                                                value={expenseData.middlemanContractPrice || ''}
                                                onChange={(e) => {
                                                    let value = e.target.value;
                                                    value = value.replace(/[^0-9.]/g, '');
                                                    const decimalCheck = value.split('.');
                                                    if (decimalCheck.length > 2) value = decimalCheck[0] + '.' + decimalCheck.slice(1).join('');
                                                    if (decimalCheck[1] && decimalCheck[1].length > 2) value = decimalCheck[0] + '.' + decimalCheck[1].slice(0, 2);

                                                    setExpenseData(prev => ({ ...prev, middlemanContractPrice: value === '' ? null : value }));
                                                    if (errors.middlemanContractPrice) setErrors(prev => ({ ...prev, middlemanContractPrice: '' }));
                                                }}
                                                placeholder="Örn: 1500.50"
                                                invalid={!!errors.middlemanContractPrice}
                                                disabled={readOnlyView || expenseData.isMiddlemanContractBased === true}
                                            />
                                            <FormFeedback>{errors.middlemanContractPrice}</FormFeedback>
                                        </FormGroup>
                                    )}
                                </Col>
                            </Row>
                        </>
                    )}
                    {((callformId && mode === 'add') || (mode === 'modify' && expenseData.callForm && expenseData.callForm.length > 0)) && readOnlyView && (expenseData.expenseType === 'ARACI ÖDEMESİ' || expenseData.expenseType === 'SATIN ALMA BEDELİ') && (
                        <>
                            <FormGroup>
                                <Label for="customerContractRate">Müşteri Sözleşme Oranı (%)</Label>
                                <Input type="text" value={expenseData.customerContractRate !== null ? `${expenseData.customerContractRate}%` : '-'} readOnly disabled />
                            </FormGroup>
                            <FormGroup>
                                <Label>Aracı Ödeme Türü</Label>
                                <Input type="text" value={expenseData.isMiddlemanContractBased ? 'Oran Bazlı' : 'Fiyat Bazlı'} readOnly disabled />
                            </FormGroup>
                            {expenseData.isMiddlemanContractBased ? (
                                <FormGroup>
                                    <Label for="middlemanContractRate">Aracı Sözleşme Oranı (%)</Label>
                                    <Input type="text" value={expenseData.middlemanContractRate !== null ? `${expenseData.middlemanContractRate}%` : '-'} readOnly disabled />
                                </FormGroup>
                            ) : (
                                <FormGroup>
                                    <Label for="middlemanContractPrice">Aracı Sözleşme Fiyatı (₺)</Label>
                                    <Input type="text" value={expenseData.middlemanContractPrice !== null ? formatCurrency(expenseData.middlemanContractPrice) : '-'} readOnly disabled />
                                </FormGroup>
                            )}
                        </>
                    )}

                    <FormGroup>
                        <Label for="description">Açıklama</Label>
                        <Input
                            type="textarea"
                            id="description"
                            name="description"
                            value={expenseData.description || ''}
                            onChange={handleInputChange}
                            placeholder="Masrafla İlgili Ek Açıklamalar (Opsiyonel)"
                            rows="3"
                            invalid={!readOnlyView && !!errors.description}
                            readOnly={readOnlyView}
                        />
                        {!readOnlyView && <FormFeedback>{errors.description}</FormFeedback>}
                    </FormGroup>
                </Form>
            </ModalBody>
            <ModalFooter>
                {readOnlyView ? (
                    <Button color="secondary" onClick={handleCancel}>
                        Kapat
                    </Button>
                ) : (
                    <>
                        <Button color="primary" onClick={handleSave}>
                            {mode === 'add' ? 'Ekle' : 'Güncelle'}
                        </Button>
                        <Button color="secondary" onClick={handleCancel} className="ms-2">
                            İptal
                        </Button>
                    </>
                )}
            </ModalFooter>
        </Modal>
    );
};

ExpenseDataModal.propTypes = {
    apiEndpoint: PropTypes.string.isRequired,
    isOpen: PropTypes.bool.isRequired,
    toggle: PropTypes.func.isRequired,
    mode: PropTypes.oneOf(['add', 'modify']).isRequired,
    selectedExpense: PropTypes.object,
    refreshData: PropTypes.func.isRequired,
    customerId: PropTypes.string,
    autoAssignCustomer: PropTypes.bool,
    accidentFile: PropTypes.arrayOf(PropTypes.string),
    callformId: PropTypes.string,
    readOnlyView: PropTypes.bool,
    initialCustomerContractRate: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    initialIsMiddlemanContractBased: PropTypes.bool,
    initialMiddlemanContractRate: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    initialMiddlemanContractPrice: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    relatedArbitrationSchema: PropTypes.arrayOf(PropTypes.string),
    arbitrationSchemaType: PropTypes.string,
};

ExpenseDataModal.defaultProps = {
    readOnlyView: false,
    initialCustomerContractRate: null,
    initialIsMiddlemanContractBased: false,
    initialMiddlemanContractRate: null,
    initialMiddlemanContractPrice: null,
    relatedArbitrationSchema: [],
    arbitrationSchemaType: null,
};

export default ExpenseDataModal;
